import React, { useRef, useEffect } from 'react';

// ─── Seeded PRNG (stable geometry across renders) ────────────────────────────
const seededRand = (seed) => {
    let s = seed | 0;
    return () => {
        s = (Math.imul(1664525, s) + 1013904223) | 0;
        return (s >>> 0) / 4294967296;
    };
};

// ─── Layer Generators (span exactly W pixels) ────────────────────────────────
const generateSkyline = (w) => {
    const r = seededRand(42);
    const items = [];
    let x = 0;
    while (x < w) {
        const bw = 16 + r() * 52;
        items.push({ x, w: bw, h: 28 + r() * 105 });
        x += bw + r() * 5 + 2;
    }
    return items;
};

const generateMidLayer = (w) => {
    const r = seededRand(99);
    const items = [];
    let x = 0;
    while (x < w) {
        const t = r();
        const type = t < 0.42 ? 'tree' : t < 0.74 ? 'car' : 'pole';
        items.push({ x, type, h: 16 + r() * 34, w: type === 'car' ? 26 + r() * 12 : type === 'pole' ? 3 : 6 });
        x += 22 + r() * 55;
    }
    return items;
};

// ─── Seamless Scroll Draw ─────────────────────────────────────────────────────
const scrollDraw = (items, scroll, w, drawFn) => {
    items.forEach(item => {
        for (const off of [0, w]) {
            const bx = item.x - scroll + off;
            if (bx + (item.w || 20) + 20 > 0 && bx - 20 < w) drawFn(bx, item);
        }
    });
};

// ─── Sky Gradient ─────────────────────────────────────────────────────────────
const buildSkyGrad = (ctx, w, h, hours) => {
    const g = ctx.createLinearGradient(0, 0, 0, h);
    if (hours >= 21 || hours < 5) {           // night
        g.addColorStop(0, '#000000'); g.addColorStop(1, '#0c0704');
    } else if (hours < 7) {                    // dawn
        g.addColorStop(0, '#1a0500'); g.addColorStop(0.6, '#3d1000'); g.addColorStop(1, '#1a0500');
    } else if (hours < 18) {                   // day
        g.addColorStop(0, '#0c0a09'); g.addColorStop(1, '#1c1410');
    } else {                                   // dusk
        g.addColorStop(0, '#1a0300'); g.addColorStop(0.5, '#401000'); g.addColorStop(1, '#1a0300');
    }
    return g;
};

// ─── Compound Silhouettes ─────────────────────────────────────────────────────
const drawCompound = (ctx, id, cx, gy) => {
    ctx.save();
    switch (id) {
        case 'bunker': {
            ctx.fillStyle = '#1a1005';
            ctx.fillRect(cx - 74, gy - 30, 148, 30);
            ctx.fillStyle = '#2d1a08';
            ctx.fillRect(cx - 80, gy - 36, 160, 8);
            // Turrets
            ctx.fillStyle = '#3d2d18';
            ctx.fillRect(cx - 87, gy - 52, 15, 16);
            ctx.fillRect(cx + 72, gy - 52, 15, 16);
            // Gun barrels
            ctx.fillStyle = '#1c1208';
            ctx.fillRect(cx - 96, gy - 47, 16, 4);
            ctx.fillRect(cx + 80, gy - 47, 16, 4);
            // Blast door
            ctx.fillStyle = '#0c0805';
            ctx.fillRect(cx - 19, gy - 26, 38, 22);
            ctx.strokeStyle = '#3d2d18'; ctx.lineWidth = 1.5;
            ctx.strokeRect(cx - 19, gy - 26, 38, 22);
            // Bolt rivets
            ctx.fillStyle = '#f59e0b';
            [cx - 68, cx - 40, cx + 37, cx + 65].forEach(bx => ctx.fillRect(bx - 1, gy - 33, 3, 3));
            // Vent pipes
            ctx.fillStyle = '#2d1a08';
            ctx.fillRect(cx - 44, gy - 44, 6, 14);
            ctx.fillRect(cx + 38, gy - 44, 6, 14);
            break;
        }
        case 'homestead': {
            ctx.fillStyle = '#3d1800';
            ctx.fillRect(cx - 55, gy - 62, 110, 62);
            // Roof
            ctx.fillStyle = '#1c0a00';
            ctx.beginPath(); ctx.moveTo(cx - 67, gy - 62); ctx.lineTo(cx, gy - 108); ctx.lineTo(cx + 67, gy - 62); ctx.closePath(); ctx.fill();
            // Chimney
            ctx.fillStyle = '#2d1400';
            ctx.fillRect(cx + 24, gy - 120, 14, 58);
            // Smoke
            ctx.strokeStyle = 'rgba(70,50,28,0.3)'; ctx.lineWidth = 3;
            ctx.beginPath(); ctx.moveTo(cx + 31, gy - 120);
            ctx.bezierCurveTo(cx + 42, gy - 136, cx + 20, gy - 152, cx + 33, gy - 168); ctx.stroke();
            // Windows (lit amber)
            ctx.fillStyle = 'rgba(245,158,11,0.65)';
            ctx.fillRect(cx - 42, gy - 50, 22, 19);
            ctx.fillRect(cx + 20, gy - 50, 22, 19);
            // Window cross-frames
            ctx.strokeStyle = '#1c0a00'; ctx.lineWidth = 1.5;
            ctx.beginPath(); ctx.moveTo(cx - 31, gy - 50); ctx.lineTo(cx - 31, gy - 31); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx - 42, gy - 40); ctx.lineTo(cx - 20, gy - 40); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx + 31, gy - 50); ctx.lineTo(cx + 31, gy - 31); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx + 20, gy - 40); ctx.lineTo(cx + 42, gy - 40); ctx.stroke();
            // Door
            ctx.fillStyle = '#1c0a00';
            ctx.fillRect(cx - 13, gy - 38, 26, 38);
            ctx.fillStyle = '#f59e0b'; ctx.fillRect(cx + 7, gy - 22, 3, 3);
            break;
        }
        case 'cabin': {
            ctx.fillStyle = '#3d1800';
            ctx.fillRect(cx - 43, gy - 56, 86, 56);
            // Log lines
            ctx.strokeStyle = '#1c0a00'; ctx.lineWidth = 2;
            for (let i = 1; i <= 4; i++) {
                ctx.beginPath(); ctx.moveTo(cx - 43, gy - i * 12 - 8); ctx.lineTo(cx + 43, gy - i * 12 - 8); ctx.stroke();
            }
            // Steep roof
            ctx.fillStyle = '#1c0a00';
            ctx.beginPath(); ctx.moveTo(cx - 56, gy - 56); ctx.lineTo(cx, gy - 104); ctx.lineTo(cx + 56, gy - 56); ctx.closePath(); ctx.fill();
            // Windows
            ctx.fillStyle = 'rgba(245,158,11,0.55)';
            ctx.fillRect(cx - 30, gy - 42, 20, 17); ctx.fillRect(cx + 10, gy - 42, 20, 17);
            // Door
            ctx.fillStyle = '#1c0a00'; ctx.fillRect(cx - 12, gy - 34, 24, 34);
            break;
        }
        case 'suburban': {
            ctx.fillStyle = '#292524';
            ctx.fillRect(cx - 62, gy - 54, 124, 54);
            // Roof
            ctx.fillStyle = '#1c1917';
            ctx.beginPath(); ctx.moveTo(cx - 70, gy - 54); ctx.lineTo(cx, gy - 87); ctx.lineTo(cx + 70, gy - 54); ctx.closePath(); ctx.fill();
            // Boarded windows
            ctx.fillStyle = '#1c1917';
            ctx.fillRect(cx - 50, gy - 44, 28, 23); ctx.fillRect(cx + 22, gy - 44, 28, 23);
            // X-boards
            ctx.strokeStyle = '#3d2800'; ctx.lineWidth = 2;
            ctx.beginPath(); ctx.moveTo(cx - 50, gy - 44); ctx.lineTo(cx - 22, gy - 21); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx - 22, gy - 44); ctx.lineTo(cx - 50, gy - 21); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx + 22, gy - 44); ctx.lineTo(cx + 50, gy - 21); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx + 50, gy - 44); ctx.lineTo(cx + 22, gy - 21); ctx.stroke();
            // Sandbag barricade
            ctx.fillStyle = '#3d3028'; ctx.fillRect(cx - 62, gy - 18, 124, 18);
            ctx.fillStyle = '#44403c'; ctx.fillRect(cx - 60, gy - 24, 120, 8);
            // Door
            ctx.fillStyle = '#0c0a09'; ctx.fillRect(cx - 15, gy - 32, 30, 32);
            ctx.strokeStyle = '#44403c'; ctx.lineWidth = 2; ctx.strokeRect(cx - 15, gy - 32, 30, 32);
            break;
        }
        case 'urban': default: {
            // Main tower
            ctx.fillStyle = '#292524';
            ctx.fillRect(cx - 33, gy - 115, 66, 115);
            // Flanking
            ctx.fillStyle = '#1c1917';
            ctx.fillRect(cx + 37, gy - 74, 48, 74);
            ctx.fillRect(cx - 85, gy - 60, 48, 60);
            // Roof caps
            ctx.fillStyle = '#44403c';
            ctx.fillRect(cx - 35, gy - 119, 70, 6);
            ctx.fillRect(cx + 35, gy - 78, 52, 5);
            // Radio mast
            ctx.strokeStyle = '#6b4c18'; ctx.lineWidth = 2;
            ctx.beginPath(); ctx.moveTo(cx + 4, gy - 119); ctx.lineTo(cx + 4, gy - 148); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(cx - 12, gy - 133); ctx.lineTo(cx + 20, gy - 133); ctx.stroke();
            // Windows
            const litWin = new Set([0, 2, 5, 8, 10, 13, 3, 7]);
            for (let row = 0; row < 5; row++) for (let col = 0; col < 3; col++) {
                ctx.fillStyle = litWin.has(row * 3 + col) ? 'rgba(245,158,11,0.6)' : '#1c1917';
                ctx.fillRect(cx - 20 + col * 20, gy - 108 + row * 20, 12, 14);
            }
            break;
        }
    }
    ctx.restore();
};

// ─── Family Figures ───────────────────────────────────────────────────────────
const drawFigure = (ctx, x, gy, frame, isChild) => {
    const scale = isChild ? 0.62 : 1.0;
    const h = 24 * scale;
    const bw = 6 * scale;
    const sway = Math.sin(frame * 0.04) * 1.2;
    ctx.fillStyle = '#1c1917';
    ctx.fillRect(x - bw * 0.65, gy - h * 0.26, bw * 0.55, h * 0.26);
    ctx.fillRect(x + bw * 0.1, gy - h * 0.26, bw * 0.55, h * 0.26);
    ctx.fillRect(x - bw / 2, gy - h * 0.73, bw, h * 0.47);
    ctx.beginPath(); ctx.arc(x, gy - h * 0.86 + sway, bw * 0.85, 0, Math.PI * 2); ctx.fill();
    ctx.fillRect(x - bw * 1.1, gy - h * 0.69, bw * 0.44, h * 0.27);
    ctx.fillRect(x + bw * 0.66, gy - h * 0.69, bw * 0.44, h * 0.27);
};

// ─── Particle factory ─────────────────────────────────────────────────────────
const mkParticle = (type, W, H) => {
    if (type === 'dust')  return { x: W + Math.random() * 80, y: 20 + Math.random() * (H - 55), vx: -(2.5 + Math.random() * 3.5), vy: (Math.random() - 0.5) * 0.7, w: 2 + Math.random() * 4, a: 0.4 + Math.random() * 0.4 };
    if (type === 'rain')  return { x: Math.random() * (W + 60) - 30, y: -8 - Math.random() * 30, vx: 1.5, vy: 8 + Math.random() * 5, len: 8 + Math.random() * 12, a: 0.3 + Math.random() * 0.3 };
    if (type === 'ember') return { x: W / 2 + (Math.random() - 0.5) * 100, y: H * 0.65, vx: (Math.random() - 0.5) * 1.5, vy: -(0.8 + Math.random() * 2), r: 1 + Math.random() * 2.5, a: 0.75 };
    return null;
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SceneCanvas({ gameState }) {
    const canvasRef = useRef(null);
    const animRef   = useRef(null);
    const gsRef     = useRef(gameState);

    useEffect(() => { gsRef.current = gameState; }, [gameState]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const W = canvas.width;
        const H = canvas.height;
        const GY = H - 28;   // ground y
        const CX = W / 2;

        const skyline  = generateSkyline(W);
        const midLayer = generateMidLayer(W);

        const s = { farOff: 0, midOff: 0, nearOff: 0, frame: 0, particles: [], weather: null };

        const loop = () => {
            const gs = gsRef.current;
            const hours     = gs.hours || 0;
            const compound  = gs.compound?.id || 'urban';
            const family    = gs.family || [];
            const evtId     = gs.pendingEvent?.id || null;

            // Scroll
            s.farOff  = (s.farOff  + 0.12) % W;
            s.midOff  = (s.midOff  + 0.35) % W;
            s.nearOff = (s.nearOff + 0.75) % W;
            s.frame   = (s.frame + 1) % 10000;

            // Weather
            const wtype = evtId === 'dust_storm'           ? 'dust'
                        : evtId === 'water_contamination'   ? 'rain'
                        : (evtId === 'structural_damage' || evtId === 'hostile_contact') ? 'ember'
                        : null;

            if (wtype) {
                if (s.particles.length < 85) for (let i = 0; i < 3; i++) s.particles.push(mkParticle(wtype, W, H));
            } else {
                s.particles = [];
            }
            s.particles = s.particles.filter(p => {
                if (wtype === 'dust')  { p.x += p.vx; p.y += p.vy; return p.x > -20; }
                if (wtype === 'rain')  { p.x += p.vx; p.y += p.vy; return p.y < H + 20; }
                if (wtype === 'ember') { p.x += p.vx; p.y += p.vy; p.a -= 0.007; return p.a > 0.04; }
                return false;
            });

            // ── DRAW ──────────────────────────────────────────────────────────

            // Sky
            ctx.fillStyle = buildSkyGrad(ctx, W, H, hours);
            ctx.fillRect(0, 0, W, H);

            // Stars + crescent moon (night)
            if (hours >= 20 || hours < 6) {
                const sr = seededRand(77);
                for (let i = 0; i < 55; i++) {
                    const sx = sr() * W, sy = sr() * GY * 0.52;
                    const twinkle = i % 7 === 0 ? 0.35 + 0.3 * Math.sin(s.frame * 0.07 + i) : 0.5;
                    ctx.globalAlpha = twinkle;
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(sx, sy, 1, 1);
                }
                ctx.globalAlpha = 1;
                // crescent moon
                ctx.fillStyle = 'rgba(210,195,165,0.1)';
                ctx.beginPath(); ctx.arc(W * 0.82, H * 0.12, 26, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = buildSkyGrad(ctx, W, H, hours); // erase part for crescent
                ctx.beginPath(); ctx.arc(W * 0.82 + 10, H * 0.12, 26, 0, Math.PI * 2); ctx.fill();
            }

            // Dawn / dusk horizon glow
            if ((hours >= 5 && hours < 8) || (hours >= 17 && hours < 21)) {
                const glow = ctx.createLinearGradient(0, GY * 0.35, 0, GY);
                glow.addColorStop(0, 'rgba(200,55,0,0)');
                glow.addColorStop(1, 'rgba(200,55,0,0.16)');
                ctx.fillStyle = glow;
                ctx.fillRect(0, 0, W, H);
            }

            // Far silhouette (ruined city)
            ctx.fillStyle = '#110802';
            scrollDraw(skyline, s.farOff, W, (bx, item) => {
                ctx.fillRect(bx, GY * 0.83 - item.h, item.w, item.h);
            });

            // Mid layer (debris, dead trees, wrecked cars)
            scrollDraw(midLayer, s.midOff, W, (bx, item) => {
                ctx.fillStyle = '#1e0e05';
                drawMidItem(ctx, bx, item, GY);
            });

            // Ground fill
            ctx.fillStyle = '#0c0905';
            ctx.fillRect(0, GY, W, H - GY);
            // Ground line
            ctx.strokeStyle = '#3d2d18'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(0, GY); ctx.lineTo(W, GY); ctx.stroke();

            // Fence (scrolling barbed wire posts)
            const postSpacing = 42;
            const postOff = s.nearOff % postSpacing;
            ctx.fillStyle = '#2d2418';
            for (let px = -postSpacing + postOff; px < W + postSpacing; px += postSpacing) {
                ctx.fillRect(px - 2, GY - 32, 4, 32);
                ctx.fillRect(px - 8, GY - 28, 16, 3);
                // Barbs
                ctx.fillRect(px - 10, GY - 28, 4, 2);
                ctx.fillRect(px + 6, GY - 28, 4, 2);
            }
            ctx.strokeStyle = '#2d2418'; ctx.lineWidth = 1;
            ctx.setLineDash([5, 4]);
            ctx.beginPath(); ctx.moveTo(0, GY - 20); ctx.lineTo(W, GY - 20); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(0, GY - 11); ctx.lineTo(W, GY - 11); ctx.stroke();
            ctx.setLineDash([]);

            // Compound (centered, static)
            drawCompound(ctx, compound, CX, GY);

            // Family silhouettes
            const fpositions = [CX - 97, CX + 102, CX - 80, CX + 84, CX - 118, CX + 122];
            family.slice(0, Math.min(4, family.length)).forEach((member, i) => {
                drawFigure(ctx, fpositions[i], GY, s.frame + i * 19, member.ageCategory === 'child');
            });

            // Weather particles
            if (s.particles.length > 0) {
                s.particles.forEach(p => {
                    ctx.globalAlpha = p.a;
                    if (wtype === 'dust') {
                        ctx.fillStyle = '#9d5c2a';
                        ctx.fillRect(p.x, p.y, p.w * 2, p.w * 0.6);
                    } else if (wtype === 'rain') {
                        ctx.strokeStyle = '#6090a0'; ctx.lineWidth = 1;
                        ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + p.vx * 1.5, p.y + p.len); ctx.stroke();
                    } else if (wtype === 'ember') {
                        ctx.fillStyle = '#f59e0b';
                        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
                    }
                });
                ctx.globalAlpha = 1;
            }

            // CRT scanlines
            ctx.fillStyle = 'rgba(0,0,0,0.03)';
            for (let y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);

            // Vignette
            const vig = ctx.createRadialGradient(CX, H / 2, H * 0.2, CX, H / 2, H * 0.85);
            vig.addColorStop(0, 'rgba(0,0,0,0)');
            vig.addColorStop(1, 'rgba(0,0,0,0.55)');
            ctx.fillStyle = vig;
            ctx.fillRect(0, 0, W, H);

            animRef.current = requestAnimationFrame(loop);
        };

        animRef.current = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(animRef.current);
    }, []); // runs once — reads live state via gsRef

    return (
        <canvas
            ref={canvasRef}
            width={600}
            height={220}
            className="w-full h-full"
            style={{ imageRendering: 'pixelated' }}
        />
    );
}

// ─── Mid layer draw helper (hoisted outside loop) ────────────────────────────
function drawMidItem(ctx, x, item, gy) {
    if (item.type === 'tree') {
        ctx.fillRect(x - 2, gy - item.h, 5, item.h);
        ctx.fillRect(x - 12, gy - item.h * 0.68, item.h * 0.32, 3);
        ctx.fillRect(x - 8,  gy - item.h * 0.46, item.h * 0.20, 2.5);
    } else if (item.type === 'car') {
        ctx.fillRect(x, gy - 13, item.w, 13);
        ctx.fillRect(x + item.w * 0.14, gy - 22, item.w * 0.64, 10);
        ctx.fillStyle = '#0c0a09';
        ctx.beginPath(); ctx.arc(x + 7, gy - 2, 5, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(x + item.w - 7, gy - 2, 5, 0, Math.PI * 2); ctx.fill();
    } else {
        // utility pole
        ctx.fillRect(x - 1.5, gy - item.h, 3, item.h);
        ctx.fillRect(x - 9, gy - item.h + 5, 18, 2);
    }
}
